// PageContainer.tsx
// Canonical page-content wrapper for BlueRock Console pages. Owns the
// inset/max-width contract so every consumer page looks consistent inside
// AppShell. AppShell wraps its `children` in PageContainer automatically;
// you only render this directly outside an AppShell, or via AppShell's
// `pageContainer={{ ... }}` prop when you want to pass options (maxWidth,
// className, etc.).

import type { CSSProperties, ReactNode } from 'react';
import './page-container.css';

export interface PageContainerProps {
  children: ReactNode;
  /** Opt-in narrow reading column. When set, caps page content at this many
   *  pixels and centers it. When omitted (default), the page fills the
   *  available width up to AppShell.Content's outer cap, left-aligned. */
  maxWidth?: number;
  /** Extra styles merged onto the container. Avoid using this for padding or
   *  max-width — that's what the component is for. */
  style?: CSSProperties;
  className?: string;
}

export function PageContainer({
  children,
  maxWidth,
  style,
  className,
}: PageContainerProps) {
  const mergedClassName = className
    ? `br-page-container ${className}`
    : 'br-page-container';
  return (
    <div
      className={mergedClassName}
      style={{
        // 12px top, 24px on the other three sides. Top is tighter because
        // AppShell.Header sits right above this with its own visual weight;
        // 24px on the sides matches the header's left/right rail. 12 (not
        // 16) aligns the page title with the sidebar's brand logo/toggle
        // (Brand.tsx's icon center lands at y=27 — see AppShell.tsx's
        // floating-toggle comment).
        padding: '12px 24px 24px',
        boxSizing: 'border-box',
        width: '100%',
        ...(maxWidth !== undefined
          ? { maxWidth, marginLeft: 'auto', marginRight: 'auto' }
          : null),
        ...style,
      }}
    >
      {children}
    </div>
  );
}
